import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Play, Pause, FileText, Volume2, VolumeX } from 'lucide-react'
import { loadPaper, loadSuno, buildPaperId, getNeuripsUrl } from '../lib/data'
import type { PaperWithSong, SunoClip } from '../lib/types'
import { LatexText } from '../lib/latex'
import ShareButton from '../components/ShareButton'
import Header from '../components/Header'
import { usePlayer } from '../lib/player'

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function PaperSong() {
  const { type, id } = useParams<{ type: string; id: string; location?: string }>()
  const [paper, setPaper] = useState<PaperWithSong | null>(null)
  const [suno, setSuno] = useState<SunoClip | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFullAbstract, setShowFullAbstract] = useState(false)
  
  // Global player context for volume sync and stopping playback
  const { pause: pauseGlobal, volume: globalVolume, setVolume: setGlobalVolume } = usePlayer()
  
  // Local player state for this page
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isAudioLoading, setIsAudioLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const paperId = type && id ? buildPaperId(type, id) : null
  
  // Stop global player when entering this page
  useEffect(() => {
    pauseGlobal()
  }, [pauseGlobal])

  useEffect(() => {
    if (!paperId) {
      setError('Invalid paper ID')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    Promise.all([
      loadPaper(paperId),
      loadSuno(paperId),
    ])
      .then(([paperData, sunoData]) => {
        if (!paperData) {
          setError('Paper not found')
        } else {
          setPaper(paperData)
          setSuno(sunoData)
          if (sunoData?.duration) setDuration(sunoData.duration)
        }
        setLoading(false)
      })
      .catch((e) => {
        setError(e.message)
        setLoading(false)
      })
  }, [paperId])

  // Audio event listeners - run when suno data is loaded
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !suno?.audio_url) return

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration)
      }
    }
    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration)
      }
    }
    const handleEnded = () => setIsPlaying(false)
    const handleWaiting = () => setIsAudioLoading(true)
    const handlePlaying = () => { setIsAudioLoading(false); setIsPlaying(true) }
    const handlePause = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('waiting', handleWaiting)
      audio.removeEventListener('playing', handlePlaying)
      audio.removeEventListener('pause', handlePause)
    }
  }, [suno?.audio_url])

  // Sync local audio volume with global volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = globalVolume
    }
  }, [globalVolume])

  const handlePlayClick = () => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const newTime = Math.max(0, Math.min(duration, percent * duration))
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-accent-500 border-t-transparent" />
      </div>
    )
  }

  if (error || !paper) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="w-20 h-20 rounded-full bg-surface-800 flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-surface-200 mb-2">Paper not found</h1>
        <p className="text-surface-400 mb-8">{error || 'The requested paper could not be found.'}</p>
        <Link 
          to="/"
          className="px-6 py-3 bg-accent-600 hover:bg-accent-500 rounded-xl font-medium transition-colors"
        >
          Browse all papers
        </Link>
      </div>
    )
  }

  const abstractPreview = paper.abstract.length > 300 
    ? paper.abstract.slice(0, 300) + '...' 
    : paper.abstract
  
  const coverUrl = suno?.image_large_url || suno?.image_url
  const songTitle = suno?.title || paper.song?.title
  const lyrics = paper.song?.lyrics || ''
  const tags = paper.song?.style || suno?.display_tags || ''
  const audioUrl = suno?.audio_url
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="h-full lg:overflow-y-auto">
      <Header />
      
      {/* Hidden audio element - always render if we have a URL */}
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}

      {/* Back link */}
      <nav className="max-w-4xl mx-auto px-4 pt-4">
        <Link 
          to="/"
          className="inline-flex items-center gap-1.5 text-surface-400 hover:text-surface-200 transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          Back to search
        </Link>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-4">
        {/* Paper header - compact */}
        <div className="mb-6">
          {/* Top row: type + buttons */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 bg-surface-800 text-surface-300 rounded-lg text-sm font-medium">
              {paper.type}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <ShareButton paperId={paperId!} />
              <a
                href={getNeuripsUrl(paperId!)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 h-8 rounded-lg text-sm text-surface-300 hover:text-surface-100 bg-surface-800 hover:bg-surface-700 transition-colors"
              >
                <ExternalLink size={14} />
                <span>NeurIPS</span>
              </a>
            </div>
          </div>
          
          <h1 className="text-2xl md:text-3xl font-bold text-surface-50 mb-2 leading-tight">
            <LatexText>{paper.title}</LatexText>
          </h1>
          
          <p className="text-surface-400 text-sm mb-3">
            {paper.authors || 'Authors not listed'}
          </p>

          {/* Abstract - compact */}
          <div className="text-surface-300 text-sm leading-relaxed">
            <LatexText as="span">
              {showFullAbstract ? paper.abstract : abstractPreview}
            </LatexText>
            {paper.abstract.length > 300 && (
              <button
                onClick={() => setShowFullAbstract(!showFullAbstract)}
                className="ml-2 text-accent-400 hover:text-accent-300 font-medium"
              >
                {showFullAbstract ? 'Less' : 'More'}
              </button>
            )}
          </div>
        </div>

        {/* Song section */}
        {(paper.song || suno) && (
          <section className="glass-card rounded-2xl overflow-hidden">
            {/* Song header with cover and info */}
            <div className="p-5">
              <div className="flex gap-5">
                {/* Cover art with play button */}
                <button
                  onClick={handlePlayClick}
                  className="relative w-28 h-28 md:w-36 md:h-36 rounded-xl overflow-hidden flex-shrink-0 group focus:outline-none focus:ring-2 focus:ring-accent-400"
                >
                  {coverUrl ? (
                    <img 
                      src={coverUrl} 
                      alt={songTitle || 'Song cover'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-surface-700 to-surface-800 flex items-center justify-center">
                      <svg className="w-12 h-12 text-surface-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                      </svg>
                    </div>
                  )}
                  
                  {/* Play overlay - always visible */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    {isAudioLoading ? (
                      <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-accent-500 flex items-center justify-center shadow-lg">
                        {isPlaying ? (
                          <Pause size={24} className="text-surface-950" fill="currentColor" />
                        ) : (
                          <Play size={24} className="text-surface-950 ml-1" fill="currentColor" />
                        )}
                      </div>
                    )}
                  </div>
                </button>

                {/* Song info */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <h2 className="text-xl md:text-2xl font-bold text-surface-100 mb-2 line-clamp-2">
                    {songTitle || 'Untitled Song'}
                  </h2>
                  
                  {tags && (
                    <p className="text-surface-400 text-sm mb-3">
                      {tags}
                    </p>
                  )}

                  {/* Suno link */}
                  {suno?.id && (
                    <a
                      href={`https://suno.com/song/${suno.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-surface-400 hover:text-surface-200 text-sm transition-colors"
                    >
                      <ExternalLink size={14} />
                      Listen on Suno
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Playbar */}
            {audioUrl && (
              <div className="px-5 pb-4">
                <div className="flex items-center gap-3">
                  <span className="text-surface-400 text-xs tabular-nums w-10 text-right">
                    {formatTime(currentTime)}
                  </span>
                  <div 
                    className="flex-1 h-1.5 bg-surface-700 rounded-full cursor-pointer group relative"
                    onClick={handleSeek}
                  >
                    <div 
                      className="h-full bg-accent-500 rounded-full relative"
                      style={{ width: `${progress}%` }}
                    >
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-accent-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
                    </div>
                  </div>
                  <span className="text-surface-400 text-xs tabular-nums w-10">
                    {formatTime(duration)}
                  </span>
                  
                  {/* Volume control */}
                  <div className="hidden md:flex items-center gap-2 ml-2">
                    <button
                      onClick={() => setGlobalVolume(globalVolume > 0 ? 0 : 1)}
                      className="text-surface-400 hover:text-surface-200 transition-colors"
                      aria-label={globalVolume > 0 ? 'Mute' : 'Unmute'}
                    >
                      {globalVolume > 0 ? <Volume2 size={18} /> : <VolumeX size={18} />}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={globalVolume}
                      onChange={(e) => setGlobalVolume(parseFloat(e.target.value))}
                      className="w-20 h-1 rounded-full appearance-none cursor-pointer
                        [&::-webkit-slider-thumb]:appearance-none
                        [&::-webkit-slider-thumb]:w-3
                        [&::-webkit-slider-thumb]:h-3
                        [&::-webkit-slider-thumb]:rounded-full
                        [&::-webkit-slider-thumb]:bg-surface-100
                        [&::-webkit-slider-thumb]:hover:bg-white
                        [&::-webkit-slider-thumb]:transition-colors
                        [&::-webkit-slider-thumb]:mt-[-3px]
                        [&::-moz-range-thumb]:w-3
                        [&::-moz-range-thumb]:h-3
                        [&::-moz-range-thumb]:rounded-full
                        [&::-moz-range-thumb]:bg-surface-100
                        [&::-moz-range-thumb]:border-0"
                      style={{
                        background: `linear-gradient(to right, #f59e0b ${globalVolume * 100}%, #334155 ${globalVolume * 100}%)`
                      }}
                      aria-label="Volume"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Lyrics - always shown */}
            {lyrics && (
              <div className="px-5 pb-5 pt-2 border-t border-surface-700/50">
                <div className="flex items-center gap-2 mb-3">
                  <FileText size={16} className="text-surface-500" />
                  <h3 className="text-sm font-medium text-surface-400">Lyrics</h3>
                </div>
                <pre className="text-surface-200 text-sm whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-y-auto">
                  {lyrics}
                </pre>
              </div>
            )}
          </section>
        )}

        {/* No song placeholder */}
        {!paper.song && !suno && (
          <section className="glass-card rounded-2xl p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-800 flex items-center justify-center">
              <svg className="w-8 h-8 text-surface-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-surface-300 mb-2">Song coming soon</h2>
            <p className="text-surface-500">The song for this paper is being generated.</p>
          </section>
        )}
      </main>
    </div>
  )
}
